import {ref, type Ref} from 'vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLoadingSpinner} from '@/ts/composables/pure/use-loading-notification';
import type {ValidateVoucherResponse} from '@/ts/types/dto/sign-in-dto';
import {useI18n} from 'vue-i18n';
import {AppError} from '@/ts/utils/errors';
import {ErrorCodesDto} from '@/ts/types/dto/error-codes-dto';

/** Return value of the useValidateVoucher composable with validation state and methods */
export interface UseValidateVoucherReturn {
  /** Reactive boolean indicating whether the voucher has been successfully validated */
  isValidated: Ref<boolean>;
  /** Reactive string containing any validation error message to display to the user */
  validationError: Ref<string>;
  /** Reactive reference to the full validation response from the API, null if not validated */
  validationResponse: Ref<ValidateVoucherResponse | null>;
  /** 
   * Validates a voucher code against the backend API
   * @param voucherCode - The voucher code string to validate
   * @returns Promise that resolves to the validation response
   * @throws Error if validation fails or voucher is invalid
   */
  validateVoucher(voucherCode: string): Promise<ValidateVoucherResponse>;
  /** Resets all validation state to initial values (not validated, no errors) */
  resetValidation(): void;
  /** 
   * Determines if the submit button should be enabled based on voucher code and validation state
   * @param voucherCode - The current voucher code input value
   * @returns true if voucher can be submitted (has content and not already validated)
   */
  canSubmit(voucherCode: string): boolean;
}

/**
 * Composable for validating voucher codes against the backend API.
 * Manages validation state, error handling, and loading indicators.
 */
export function useValidateVoucher(): UseValidateVoucherReturn {
  const {t} = useI18n();
  const api = useApi();
  
  const isValidated = ref<boolean>(false);
  const validationError = ref<string>('');
  const validationResponse = ref<ValidateVoucherResponse | null>(null);

  const [validateVoucherInternal] = useLoadingSpinner(
    async(voucherCode: string) => {
      const trimmedCode = voucherCode?.trim() ?? '';
      if (!trimmedCode) {
        throw new AppError('Voucher code is required', ErrorCodesDto.INVALID_VOUCHER_CODE);
      }

      let response: ValidateVoucherResponse;
      try {
        response = await api.public.validateVoucher({voucherCode: trimmedCode});
      } catch (error: unknown) {
        isValidated.value = false;
        validationError.value = t('invalid_voucher_code');
        validationResponse.value = null;
        throw error;
      }

      if (response.valid) {
        isValidated.value = true;
        validationError.value = '';
        validationResponse.value = response;
        return response;
      }

      isValidated.value = false;
      validationError.value = t('invalid_voucher_code');
      validationResponse.value = null;
      throw new AppError(response.failureReason ?? 'Invalid voucher code', ErrorCodesDto.INVALID_VOUCHER_CODE);
    },
  );

  const validateVoucher = async(voucherCode: string): Promise<ValidateVoucherResponse> => {
    await validateVoucherInternal(voucherCode);
    if (!validationResponse.value) {
      throw new AppError('Validation failed - no response received', ErrorCodesDto.UNKNOWN);
    }
    return validationResponse.value;
  };

  const resetValidation = (): void => {
    isValidated.value = false;
    validationError.value = '';
    validationResponse.value = null;
  };

  const canSubmit = (voucherCode: string): boolean => {
    return (voucherCode?.trim()?.length ?? 0) > 0 && !isValidated.value;
  };

  return {
    isValidated,
    validationError,
    validationResponse,
    validateVoucher,
    resetValidation,
    canSubmit,
  };
}
